
import { useState, useEffect, useRef } from 'react';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';

export const useMessageNotifications = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const subscriptionRef = useRef<(() => void) | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clean up subscription
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current();
        } catch (error) {
        }
        subscriptionRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Clean up previous subscription if user changed
    if (currentUserIdRef.current !== user?.id) {
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current();
        } catch (error) {
        }
        subscriptionRef.current = null;
      }
      currentUserIdRef.current = user?.id || null;
    }

    if (user?.id && pb.authStore.isValid) {
      fetchUnreadCount();
      setupSubscription();
    } else {
      setUnreadCount(0);
    }
  }, [user?.id, pb.authStore.isValid]);

  const fetchUnreadCount = async () => {
    if (!user?.id || !pb.authStore.isValid || !isMountedRef.current) return;

    try {
      // Get unread messages where the user is the receiver
      const unreadMessages = await pb.collection('messages').getList(1, 1, {
        filter: `receiverId = "${user.id}" && isRead = false`,
      });

      if (isMountedRef.current) {
        setUnreadCount(unreadMessages.totalItems);
      }
    } catch (error: any) {
    }
  };

  const setupSubscription = async () => {
    if (!user?.id || !pb.authStore.isValid || !isMountedRef.current) return;

    try {
      const unsubscribe = await pb.collection('messages').subscribe('*', (e) => {
        if (!isMountedRef.current) return;
        
        // If it's a new message for this user and it's unread
        if (e.action === 'create' && e.record.receiverId === user.id && !e.record.isRead) {
          setUnreadCount(prev => prev + 1);
        }
        
        // If a message was marked as read
        if (e.action === 'update' && e.record.receiverId === user.id && e.record.isRead) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      });

      if (isMountedRef.current) {
        subscriptionRef.current = unsubscribe;
      }
    } catch (error: any) {
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id || !pb.authStore.isValid) return;

    try {
      // This would need to be implemented as a batch operation
      // For now, we'll just reset the count
      setUnreadCount(0);
    } catch (error) {
    }
  };

  return {
    unreadCount,
    markAllAsRead,
  };
};
